import React, { useState, useCallback } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Badge,
  Divider,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { usePermissions } from "../../../hooks/usePermissions";
import { DOCUMENT_PERMISSIONS } from "../../../utils/documentPermissions";

/**
 * Recursive tree node component.
 * Renders a single folder with expand/collapse and right-click context menu.
 */
function FolderTreeNode({
  folder,
  depth,
  selectedFolderId,
  expandedIds,
  onToggleExpand,
  onSelectFolder,
  onContextMenu,
}) {
  const isSelected = selectedFolderId === folder.id;
  const isExpanded = expandedIds.has(folder.id);
  const hasChildren = Array.isArray(folder.children) && folder.children.length > 0;
  const childCount = folder.children?.length || 0;

  const handleClick = () => {
    onSelectFolder(folder.id);
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    onToggleExpand(folder.id);
  };

  const handleRightClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu(e, folder);
  };

  return (
    <>
      <ListItemButton
        selected={isSelected}
        onClick={handleClick}
        onContextMenu={handleRightClick}
        sx={{
          pl: 2 + depth * 2,
          py: 0.5,
          borderRadius: 1,
          mx: 0.5,
          mb: 0.25,
          "&.Mui-selected": {
            backgroundColor: "action.selected",
          },
        }}
      >
        {/* Expand/collapse toggle */}
        <IconButton
          size="small"
          onClick={handleToggle}
          sx={{
            mr: 0.5,
            visibility: hasChildren ? "visible" : "hidden",
            p: 0.25,
          }}
        >
          {isExpanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>

        {/* Folder icon */}
        <ListItemIcon sx={{ minWidth: 32 }}>
          {isExpanded && hasChildren ? (
            <FolderOpenIcon color="primary" fontSize="small" />
          ) : (
            <FolderIcon color="action" fontSize="small" />
          )}
        </ListItemIcon>

        {/* Folder name + child count badge */}
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: isSelected ? 600 : 400,
                  maxWidth: 140,
                }}
              >
                {folder.name}
              </Typography>
              {childCount > 0 && (
                <Badge
                  badgeContent={childCount}
                  color="default"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.65rem",
                      height: 16,
                      minWidth: 16,
                      backgroundColor: "grey.300",
                      color: "text.secondary",
                    },
                  }}
                />
              )}
            </Box>
          }
          secondary={
            folder.creatorName ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {folder.creatorName}
              </Typography>
            ) : null
          }
        />
      </ListItemButton>

      {/* Render children recursively */}
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {folder.children.map((child) => (
              <FolderTreeNode
                key={child.id}
                folder={child}
                depth={depth + 1}
                selectedFolderId={selectedFolderId}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
                onSelectFolder={onSelectFolder}
                onContextMenu={onContextMenu}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

/**
 * FolderTree — Sidebar component rendering hierarchical folder structure.
 *
 * Props:
 * - folders: Array of folder tree nodes (each with id, name, children[], creatorName)
 * - selectedFolderId: Currently selected folder ID
 * - onSelectFolder: (folderId) => void
 * - onCreateSubFolder: (parentFolder) => void
 * - onRenameFolder: (folder) => void
 * - onDeleteFolder: (folder) => void
 * - onMoveFolder: (folder) => void
 */
const FolderTree = ({
  folders = [],
  selectedFolderId,
  onSelectFolder,
  onCreateSubFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveFolder,
}) => {
  const { hasPermission } = usePermissions();
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null); // { mouseX, mouseY, folder }

  const canUpload = hasPermission(DOCUMENT_PERMISSIONS.UPLOAD);
  const canEdit = hasPermission(DOCUMENT_PERMISSIONS.EDIT);
  const canDelete = hasPermission(DOCUMENT_PERMISSIONS.DELETE);

  const handleToggleExpand = useCallback((folderId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const handleContextMenu = useCallback((event, folder) => {
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      folder,
    });
  }, []);

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleCreateSubFolder = () => {
    if (contextMenu?.folder && onCreateSubFolder) {
      onCreateSubFolder(contextMenu.folder);
    }
    handleCloseContextMenu();
  };

  const handleRenameFolder = () => {
    if (contextMenu?.folder && onRenameFolder) {
      onRenameFolder(contextMenu.folder);
    }
    handleCloseContextMenu();
  };

  const handleMoveFolder = () => {
    if (contextMenu?.folder && onMoveFolder) {
      onMoveFolder(contextMenu.folder);
    }
    handleCloseContextMenu();
  };

  const handleDeleteFolder = () => {
    if (contextMenu?.folder && onDeleteFolder) {
      onDeleteFolder(contextMenu.folder);
    }
    handleCloseContextMenu();
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "auto",
      }}
    >
      <List component="nav" dense>
        {folders.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2, textAlign: "center" }}
          >
            No folders yet
          </Typography>
        ) : (
          folders.map((folder) => (
            <FolderTreeNode
              key={folder.id}
              folder={folder}
              depth={0}
              selectedFolderId={selectedFolderId}
              expandedIds={expandedIds}
              onToggleExpand={handleToggleExpand}
              onSelectFolder={onSelectFolder}
              onContextMenu={handleContextMenu}
            />
          ))
        )}
      </List>

      {/* Right-click context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {canUpload && (
          <MenuItem onClick={handleCreateSubFolder}>
            <ListItemIcon>
              <CreateNewFolderIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create sub-folder</ListItemText>
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem onClick={handleRenameFolder}>
            <ListItemIcon>
              <DriveFileRenameOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rename</ListItemText>
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem onClick={handleMoveFolder}>
            <ListItemIcon>
              <DriveFileMoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Move</ListItemText>
          </MenuItem>
        )}
        {canDelete && [
          <Divider key="divider" />,
          <MenuItem key="delete" onClick={handleDeleteFolder}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </Box>
  );
};

export default FolderTree;
